import { AuditLog } from "../../models";
import { TablePagination } from "../Common";
import { useEffect, useState } from "react";
import { format } from "date-fns/format";
import { formatCentreName } from "../../helpers";
import { AUDITLOG_TABLE_PAGE_SIZE } from "../../constants";

interface params {
	auditLogs: AuditLog[];
}

export const AuditLogsTable = ({ auditLogs }: params) => {
	const [filteredAuditLogs, setFilteredAuditLogs] = useState<AuditLog[]>([]);
	const [pagedAuditLogs, setPagedAuditLogs] = useState<AuditLog[]>([]);
	const [patientSearchText, setPatientSearchText] = useState<string>("");
	const [doctorSearchText, setDoctorSearchText] = useState<string>("");
	const [userSearchText, setUserSearchText] = useState<string>("");

	useEffect(() => {
		let filteredLogs = auditLogs;

		if (patientSearchText) {
			filteredLogs = filteredLogs.filter(
				(log) =>
					log.patientName &&
					log.patientName
						.toLowerCase()
						.includes(patientSearchText.toLowerCase())
			);
		}

		if (doctorSearchText) {
			filteredLogs = filteredLogs.filter(
				(log) =>
					log.doctorName &&
					log.doctorName.toLowerCase().includes(doctorSearchText.toLowerCase())
			);
		}

		if (userSearchText) {
			filteredLogs = filteredLogs.filter(
				(log) =>
					log.username &&
					log.username.toLowerCase().includes(userSearchText.toLowerCase())
			);
		}

		setFilteredAuditLogs(filteredLogs);
	}, [auditLogs, patientSearchText, doctorSearchText, userSearchText]);

	const handleTableCellClick = (
		filterName: string,
		value: string,
		add: boolean
	) => {
		switch (filterName) {
			case "patient":
				setPatientSearchText(add ? value : "");
				break;

			case "doctor":
				setDoctorSearchText(add ? value : "");
				break;

			case "user":
				setUserSearchText(add ? value : "");
				break;

			default:
				break;
		}
	};

	const handlePageChange = (logs: AuditLog[]) => {
		setPagedAuditLogs(logs);
	};

	const renderFilters = () => {
		return (
			<div
				className="ch-lightgrey-background flex p-4"
				style={{ borderRadius: 12 }}
			>
				<div className="ch-em-text mt-2">Filter Results</div>

				{patientSearchText && (
					<div className="ch-chip ch-chip-grey flex gap-4 ml-4 mt-1">
						<div>{patientSearchText}</div>
						<img
							src="images/svg/close-black.svg"
							alt="clear"
							onClick={() =>
								handleTableCellClick("patient", patientSearchText, false)
							}
						/>
					</div>
				)}

				{doctorSearchText && (
					<div className="ch-chip ch-chip-grey flex gap-4 ml-4 mt-1">
						<div>{doctorSearchText}</div>
						<img
							src="images/svg/close-black.svg"
							alt="clear"
							onClick={() =>
								handleTableCellClick("doctor", doctorSearchText, false)
							}
						/>
					</div>
				)}

				{userSearchText && (
					<div className="ch-chip ch-chip-grey flex gap-4 ml-4 mt-1">
						<div>{userSearchText}</div>
						<img
							src="images/svg/close-black.svg"
							alt="clear"
							onClick={() =>
								handleTableCellClick("user", userSearchText, false)
							}
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<>
			{renderFilters()}
			<table
				aria-label="Audit logs"
				className="ch-table ch-table-selectable mt-4"
				style={{ minWidth: 650 }}
			>
				<thead>
					<tr>
						<th>Centre</th>
						<th>Doctor</th>
						<th>Patient</th>
						<th>User</th>
						<th>Application Screen</th>
						<th style={{ maxWidth: 320 }}>Action</th>
						<th>Created</th>
						<th>From Value</th>
						<th>To Value</th>
					</tr>
				</thead>
				{pagedAuditLogs && (
					<tbody>
						{pagedAuditLogs.map((log) => (
							<tr key={log.id}>
								<td>{formatCentreName(log.clinicName)}</td>
								<td
									className="selectable"
									onClick={() => {
										handleTableCellClick("doctor", log.doctorName, true);
									}}
								>
									{log.doctorName}
								</td>
								<td
									className="selectable"
									onClick={() => {
										handleTableCellClick("patient", log.patientName, true);
									}}
								>
									{log.patientName}
								</td>
								<td
									className="selectable"
									onClick={() => {
										handleTableCellClick("user", log.username, true);
									}}
								>
									{log.username}
								</td>
								<td>{log.applicationScreen}</td>
								<td style={{ maxWidth: 320 }}>{log.action}</td>
								<td>{format(log.createdOn, "dd/MM/yyyy HH:mm")}</td>
								<td>{log.fromValue}</td>
								<td>{log.toValue}</td>
							</tr>
						))}
					</tbody>
				)}
			</table>
			<TablePagination
				items={filteredAuditLogs || []}
				pageSize={AUDITLOG_TABLE_PAGE_SIZE}
				pageChange={handlePageChange}
			/>
		</>
	);
};
